import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { REGIONES, TASK_TYPES } from "@/lib/constants";

export default function DelegationCreate() {
  const nav = useNavigate();
  const { user } = useAuth();
  const notApproved = user?.role === "lawyer" && user?.verification_status !== "approved";
  const [form, setForm] = useState({
    title: "", task_type: "audiencia", court: "", case_number: "",
    region: "Metropolitana", comuna: "", address: "", date: "", time: "",
    fee_clp: 70000, notes: "",
  });
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!termsAccepted) {
      setError("Debes aceptar los Términos de Servicio para publicar la delegación.");
      return;
    }
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/delegations", { ...form, fee_clp: Number(form.fee_clp) });
      nav(`/app/delegaciones/${data.id}`);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="container-app py-12 max-w-3xl" data-testid="create-delegation-page">
      <div className="label-eyebrow">Nueva publicación</div>
      <h1 className="serif text-5xl mt-3">Publicar delegación</h1>
      <p className="mt-2 text-muted-foreground">Completa los detalles. Tarda menos de 90 segundos.</p>

      {notApproved && (
        <div className="mt-6 panel p-5 border-[hsl(var(--primary))]/40" data-testid="not-approved-banner">
          <div className="label-eyebrow text-[hsl(var(--primary))]">Cuenta en verificación</div>
          <p className="mt-2 text-sm">
            Para publicar delegaciones primero debes ser aprobado por un administrador.
            Sube tu certificado de título desde <a href="/app/perfil" className="link-underline">tu perfil</a> y
            espera la confirmación.
          </p>
        </div>
      )}

      <form onSubmit={submit} className={`mt-10 panel p-8 grid sm:grid-cols-2 gap-5 ${notApproved ? "opacity-50 pointer-events-none" : ""}`} data-testid="delegation-form" aria-disabled={notApproved}>
        <Field label="Título"><input className="field" required value={form.title} onChange={(e)=>set("title", e.target.value)} placeholder="Ej: Audiencia preparatoria - Pensión alimentos" data-testid="del-title"/></Field>
        <Field label="Tipo">
          <select className="field" value={form.task_type} onChange={(e)=>set("task_type", e.target.value)} data-testid="del-type">
            {TASK_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </Field>
        <Field label="Tribunal" className="sm:col-span-2"><input className="field" required value={form.court} onChange={(e)=>set("court", e.target.value)} data-testid="del-court"/></Field>
        <Field label="Causa / RIT"><input className="field" value={form.case_number} onChange={(e)=>set("case_number", e.target.value)} data-testid="del-case"/></Field>
        <Field label="Región">
          <select className="field" value={form.region} onChange={(e)=>set("region", e.target.value)} data-testid="del-region">
            {REGIONES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </Field>
        <Field label="Comuna"><input className="field" required value={form.comuna} onChange={(e)=>set("comuna", e.target.value)} data-testid="del-comuna"/></Field>
        <Field label="Dirección"><input className="field" value={form.address} onChange={(e)=>set("address", e.target.value)} data-testid="del-address"/></Field>
        <Field label="Fecha"><input className="field" type="date" required value={form.date} onChange={(e)=>set("date", e.target.value)} data-testid="del-date"/></Field>
        <Field label="Hora"><input className="field" type="time" required value={form.time} onChange={(e)=>set("time", e.target.value)} data-testid="del-time"/></Field>
        <Field label="Honorario (CLP)" className="sm:col-span-2">
          <input className="field" type="number" min={0} step={1000} required value={form.fee_clp} onChange={(e)=>set("fee_clp", e.target.value)} data-testid="del-fee"/>
        </Field>
        <Field label="Notas / instrucciones" className="sm:col-span-2">
          <textarea className="field" rows={4} value={form.notes} onChange={(e)=>set("notes", e.target.value)} data-testid="del-notes"/>
        </Field>
        {error && <div className="sm:col-span-2 text-sm text-[hsl(var(--destructive))]" data-testid="del-error">{error}</div>}
        <label className="sm:col-span-2 flex items-start gap-2 text-sm text-muted-foreground cursor-pointer" data-testid="del-terms-label">
          <input
            type="checkbox"
            className="mt-1"
            checked={termsAccepted}
            onChange={(e) => setTermsAccepted(e.target.checked)}
            data-testid="del-terms-checkbox"
          />
          <span>
            Declaro que la información es veraz y acepto los{" "}
            <Link to="/terminos" target="_blank" className="underline">
              Términos de Servicio
            </Link>{" "}
            de ColegaLink, incluyendo la política de pagos protegidos y cancelaciones.
          </span>
        </label>
        <div className="sm:col-span-2 flex items-center gap-3">
          <button disabled={loading || !termsAccepted} className="btn-primary disabled:opacity-60" data-testid="del-submit">{loading ? "Publicando…" : "Publicar"}</button>
          <button type="button" onClick={()=>nav(-1)} className="btn-ghost">Cancelar</button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="label-eyebrow">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
